import { StoreRole } from '@app/shared';
import { createBrowserRouter } from 'react-router-dom';
import { ErrorPage } from '@/pages/shared/error';
import { GuestOnly, RequireAuth, RequireMembership, RootRedirect } from './guards';
import { AuthLayout } from './layouts';
import { loadNamespace } from '@/shared/i18n/loadNamespace';

const OWNER_ROLES = [StoreRole.OWNER];

export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <RootRedirect /> },

      // /login, /register, /forgot-password, /reset-password — redirect away if already signed in.
      {
        element: <GuestOnly />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: 'login',
                lazy: async () => {
                  const { LoginPage } = await import('@/pages/auth/login');
                  return { Component: LoginPage };
                },
              },
              {
                path: 'register',
                lazy: async () => {
                  const { RegisterPage } = await import('@/pages/auth/register');
                  return { Component: RegisterPage };
                },
              },
              {
                path: 'forgot-password',
                lazy: async () => {
                  const { ForgotPasswordPage } = await import('@/pages/auth/forgot-password');
                  return { Component: ForgotPasswordPage };
                },
              },
              {
                path: 'reset-password',
                lazy: async () => {
                  const { ResetPasswordPage } = await import('@/pages/auth/reset-password');
                  return { Component: ResetPasswordPage };
                },
              },
            ],
          },
        ],
      },

      // Every authenticated zone.
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: 'verify',
                lazy: async () => {
                  const { VerifyPage } = await import('@/pages/auth/verify');
                  return { Component: VerifyPage };
                },
              },
            ],
          },
          {
            path: 'create-store',
            lazy: async () => {
              const { CreateStorePage } = await import('@/pages/create-store');
              return { Component: CreateStorePage };
            },
          },
          {
            path: 'admin',
            element: <RequireMembership roles={OWNER_ROLES} />,
            children: [
              {
                lazy: async () => {
                  const [{ AdminLayout }] = await Promise.all([
                    import('./layouts/AdminLayout'),
                    loadNamespace('admin'),
                  ]);
                  return { Component: AdminLayout };
                },
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { AdminDashboardPage } = await import('@/pages/admin/dashboard');
                      return { Component: AdminDashboardPage };
                    },
                  },
                  {
                    path: 'categories',
                    lazy: async () => {
                      const { CategoriesPage } = await import('@/pages/admin/categories');
                      return { Component: CategoriesPage };
                    },
                  },
                  {
                    path: 'suppliers',
                    lazy: async () => {
                      const { SuppliersPage } = await import('@/pages/admin/suppliers');
                      return { Component: SuppliersPage };
                    },
                  },
                  {
                    path: 'products',
                    lazy: async () => {
                      const { ProductsListPage } = await import('@/pages/admin/products/list');
                      return { Component: ProductsListPage };
                    },
                  },
                  {
                    path: 'products/new',
                    lazy: async () => {
                      const { ProductQuickAddPage } =
                        await import('@/pages/admin/products/quick-add');
                      return { Component: ProductQuickAddPage };
                    },
                  },
                  {
                    path: 'products/:id',
                    lazy: async () => {
                      const { ProductFormPage } = await import('@/pages/admin/products/form');
                      return { Component: ProductFormPage };
                    },
                  },
                  {
                    path: 'settings',
                    lazy: async () => {
                      const { SettingsPage } = await import('@/pages/admin/settings');
                      return { Component: SettingsPage };
                    },
                  },
                  {
                    path: 'stock',
                    lazy: async () => {
                      const { StockLevelsPage } = await import('@/pages/admin/stock/levels');
                      return { Component: StockLevelsPage };
                    },
                  },
                  {
                    path: 'stock/products/:id',
                    lazy: async () => {
                      const { StockProductPage } = await import('@/pages/admin/stock/product');
                      return { Component: StockProductPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        path: 'no-access',
        lazy: async () => {
          const { NoAccessPage } = await import('@/pages/shared/no-access');
          return { Component: NoAccessPage };
        },
      },
      {
        path: '*',
        lazy: async () => {
          const { NotFoundPage } = await import('@/pages/shared/not-found');
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
